import React, { useState } from 'react'

/**
 * Toolbar button component
 */
const ToolbarButton = ({ onClick, isActive, icon, tooltip, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        p-2 rounded transition-colors
        ${
          isActive
            ? 'bg-blue-700 text-white'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      title={tooltip}
      type="button"
    >
      {icon}
    </button>
  )
}

/**
 * Toolbar divider
 */
const ToolbarDivider = () => {
  return <div className="w-px h-6 bg-gray-700 mx-1" />
}

/**
 * Heading dropdown component
 */
const HeadingDropdown = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false)

  const headings = [
    { level: 1, label: 'Heading 1' },
    { level: 2, label: 'Heading 2' },
    { level: 3, label: 'Heading 3' },
  ]

  const currentHeading = headings.find((h) =>
    editor?.isActive('heading', { level: h.level })
  )

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 rounded text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center gap-2"
        type="button"
      >
        <span className="text-sm">
          {currentHeading ? currentHeading.label : 'Paragraph'}
        </span>
        <svg
          className="w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-20 min-w-[150px]">
            <button
              onMouseDown={(e) => {
                e.preventDefault()
                editor?.chain().focus().setParagraph().run()
                setIsOpen(false)
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
              type="button"
            >
              Paragraph
            </button>
            {headings.map((h) => (
              <button
                key={h.level}
                onMouseDown={(e) => {
                  e.preventDefault()
                  editor?.chain().focus().toggleHeading({ level: h.level }).run()
                  setIsOpen(false)
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                type="button"
              >
                {h.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Font size dropdown component
 */
const FontSizeDropdown = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false)

  const fontSizes = [
    { size: '12px', label: 'Small' },
    { size: '14px', label: 'Normal' },
    { size: '16px', label: 'Medium' },
    { size: '18px', label: 'Large' },
    { size: '24px', label: 'Huge' },
  ]

  // Get current font size from editor
  const currentFontSize = editor?.getAttributes('textStyle').fontSize
  const currentSize = fontSizes.find(f => f.size === currentFontSize)

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 rounded text-gray-300 hover:bg-gray-700 hover:text-white transition-colors flex items-center gap-2"
        type="button"
        title="Font Size"
      >
        <svg
          className="w-5 h-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="4 7 4 4 20 4 20 7" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <line x1="12" y1="4" x2="12" y2="20" />
        </svg>
        <span className="text-xs">
          {currentSize ? currentSize.label : 'Size'}
        </span>
        <svg
          className="w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-20 min-w-[120px]">
            {fontSizes.map((f) => (
              <button
                key={f.size}
                onMouseDown={(e) => {
                  e.preventDefault()
                  editor?.chain().focus().setFontSize(f.size).run()
                  setIsOpen(false)
                }}
                className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                  currentFontSize === f.size
                    ? 'bg-blue-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
                type="button"
              >
                {f.label} ({f.size})
              </button>
            ))}
            <button
              onMouseDown={(e) => {
                e.preventDefault()
                editor?.chain().focus().unsetFontSize().run()
                setIsOpen(false)
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white border-t border-gray-700"
              type="button"
            >
              Reset
            </button>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Link button component
 */
const LinkButton = ({ editor }) => {
  const [showInput, setShowInput] = useState(false)
  const [url, setUrl] = useState('')

  const handleSetLink = () => {
    if (url) {
      // Ensure URL has protocol
      let finalUrl = url.trim()
      if (!finalUrl.match(/^https?:\/\//i)) {
        finalUrl = 'https://' + finalUrl
      }

      editor
        ?.chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: finalUrl })
        .run()
    }
    setShowInput(false)
    setUrl('')
  }

  const handleUnsetLink = () => {
    editor?.chain().focus().unsetLink().run()
    setShowInput(false)
    setUrl('')
  }

  return (
    <div className="relative">
      <ToolbarButton
        onClick={() => {
          if (editor?.isActive('link')) {
            handleUnsetLink()
          } else {
            setShowInput(!showInput)
          }
        }}
        isActive={editor?.isActive('link')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
              clipRule="evenodd"
            />
          </svg>
        }
        tooltip="Add Link"
      />

      {showInput && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowInput(false)}
          />
          {/* Link input */}
          <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-20 p-3 min-w-[300px]">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-3 py-2 bg-gray-900 text-white border border-gray-700 rounded mb-2"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSetLink()
                }
              }}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSetLink}
                className="px-3 py-1 bg-blue-700 hover:bg-blue-600 rounded text-sm text-white"
                type="button"
              >
                Set Link
              </button>
              <button
                onClick={() => setShowInput(false)}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm text-white"
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Main editor toolbar component
 * Provides formatting controls for TipTap editor
 */
const EditorToolbar = ({ editor }) => {
  if (!editor) return null

  return (
    <div className="editor-toolbar sticky top-0 z-10 flex items-center gap-1 p-2 border-b border-gray-700 bg-gray-800 flex-wrap">
      {/* Text formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6zM6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
          </svg>
        }
        tooltip="Bold (Ctrl+B)"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="19" y1="4" x2="10" y2="4" />
            <line x1="14" y1="20" x2="5" y2="20" />
            <line x1="15" y1="4" x2="9" y2="20" />
          </svg>
        }
        tooltip="Italic (Ctrl+I)"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
            <line x1="4" y1="21" x2="20" y2="21" />
          </svg>
        }
        tooltip="Underline (Ctrl+U)"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="4" y1="12" x2="20" y2="12" />
            <path d="M17.5 5H9a4 4 0 0 0 0 8h6a4 4 0 0 1 0 8H6.5" />
          </svg>
        }
        tooltip="Strikethrough"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        }
        tooltip="Inline Code (`)"
      />

      <ToolbarDivider />

      {/* Headings */}
      <HeadingDropdown editor={editor} />

      {/* Font Size */}
      <FontSizeDropdown editor={editor} />

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <circle cx="3" cy="6" r="1" fill="currentColor" />
            <circle cx="3" cy="12" r="1" fill="currentColor" />
            <circle cx="3" cy="18" r="1" fill="currentColor" />
          </svg>
        }
        tooltip="Bullet List"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="10" y1="6" x2="21" y2="6" />
            <line x1="10" y1="12" x2="21" y2="12" />
            <line x1="10" y1="18" x2="21" y2="18" />
            <path d="M4 6h1v4" />
            <path d="M4 10h2" />
            <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
          </svg>
        }
        tooltip="Numbered List"
      />

      <ToolbarDivider />

      {/* More formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
            <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
          </svg>
        }
        tooltip="Quote (>)"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="2" y="3" width="20" height="18" rx="2" />
            <line x1="8" y1="7" x2="8" y2="17" />
            <line x1="12" y1="7" x2="12" y2="17" />
            <line x1="16" y1="7" x2="16" y2="17" />
          </svg>
        }
        tooltip="Code Block (```)"
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        icon={
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        }
        tooltip="Horizontal Rule (---)"
      />

      <ToolbarDivider />

      {/* Link */}
      <LinkButton editor={editor} />

      {/* Note: Image button will be added separately with upload functionality */}
    </div>
  )
}

export default EditorToolbar
